function textOf(value) {
  if (!value) return '';
  if (typeof value === 'string') return value;
  try {
    return JSON.stringify(value);
  } catch {
    return '';
  }
}

function findingText(f) {
  return [
    f?.title,
    f?.description,
    f?.module,
    f?.fingerprint,
    f?.asset,
    f?.cve,
    f?.cveId,
    textOf(f?.detail),
    textOf(f?.additionalData),
  ].filter(Boolean).join(' ');
}

function tagsOf(f) {
  const values = [
    f?.tags,
    f?.tag,
    f?.labels,
    f?.categories,
    f?.category,
    f?.detail?.tags,
    f?.detail?.labels,
    f?.detail?.categories,
    f?.additionalData?.tags,
    f?.additionalData?.labels,
    f?.additionalData?.categories,
    f?.additionalData?.shodanTags,
  ];
  return values.flatMap(value => {
    if (!value) return [];
    if (Array.isArray(value)) return value;
    if (typeof value === 'string') return value.split(/[,\s]+/);
    return [];
  }).map(value => String(value).trim().toLowerCase()).filter(Boolean);
}

function moduleOf(f) {
  return (f?.module ?? f?.sourceModule ?? f?.detail?.additionalData?.sourceModule ?? f?.additionalData?.sourceModule ?? '').toLowerCase();
}

function fingerprintOf(f) {
  return (f?.fingerprint ?? '').toLowerCase();
}

function hasAdSource(f) {
  const mod = moduleOf(f);
  const fp = fingerprintOf(f);
  return mod.startsWith('ad-')
    || mod === 'identity-review'
    || mod.includes('active-directory')
    || mod.includes('bloodhound')
    || fp.startsWith('ad-')
    || fp.startsWith('active-directory');
}

function hasCloudAssessmentSource(f) {
  const mod = moduleOf(f);
  const fp = fingerprintOf(f);
  return mod === 'm365-baseline'
    || mod === 'm365-drift'
    || mod.startsWith('cloud-')
    || mod.includes('cspm')
    || fp.startsWith('cloud-')
    || fp.startsWith('aws-')
    || fp.startsWith('azure-')
    || fp.startsWith('m365-');
}
function hasExternalScannerSource(f) {
  const mod = moduleOf(f);
  const fp = fingerprintOf(f);
  return mod === 'nessus'
    || mod === 'vuln-nessus'
    || mod === 'nessus-vuln'
    || mod.startsWith('nessus-')
    || mod.startsWith('vuln-nessus')
    || mod.includes('vulnerability-scan')
    || mod.includes('shodan')
    || fp.startsWith('nessus-')
    || fp.startsWith('vuln-nessus')
    || fp.startsWith('endpoint-cve:')
    || fp.startsWith('connector-passive-vulnerability-lookup:')
    || fp.startsWith('shodan-')
    || fp.startsWith('osint-shodan:');
}

const CVE_PATTERN = /\bCVE-\d{4}-\d{4,}\b/i;

function hasCveEvidence(f) {
  const text = findingText(f);
  const tags = tagsOf(f);
  return CVE_PATTERN.test(text) || tags.some(tag => CVE_PATTERN.test(tag));
}

export function isSddFinding(f) {
  const mod = (f?.module ?? f?.sourceModule ?? '').toLowerCase();
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const source = (f?.detail?.additionalData?.sourceModule ?? f?.additionalData?.sourceModule ?? '').toLowerCase();
  return mod.startsWith('sdd-') || fp.startsWith('sdd-') || source.startsWith('sdd-');
}

export function isNessusScanFinding(f) {
  const mod = moduleOf(f);
  const fp = fingerprintOf(f);
  return mod === 'nessus'
    || mod === 'vuln-nessus'
    || mod === 'nessus-vuln'
    || mod.startsWith('nessus-')
    || mod.startsWith('vuln-nessus')
    || fp.startsWith('nessus-')
    || fp.startsWith('vuln-nessus');
}

export function isNessusVulnerabilityFinding(f) {
  return isNessusScanFinding(f) && hasCveEvidence(f);
}

export function isShodanVulnerabilityFinding(f) {
  const mod = (f?.module ?? '').toLowerCase();
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const isShodan = mod.includes('shodan') || fp.startsWith('shodan-') || fp.startsWith('osint-shodan:');
  if (!isShodan) return false;

  // The CVE queue should only include Shodan's VULNS/CVE signal, not adjacent
  // Shodan exposure hygiene such as open services or TLS notes.
  const isShodanVulnsRecord =
    mod.includes('vulns')
    || mod.includes('vulnerab')
    || fp.includes(':vulns:')
    || fp.includes(':vuln:')
    || fp.includes(':svc-cve:')
    || fp.includes(':cve:');
  if (!isShodanVulnsRecord) return false;

  return hasCveEvidence(f);
}

export function isEndpointVulnerabilityFinding(f) {
  const mod = (f?.module ?? '').toLowerCase();
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const isEndpoint = mod === 'connector-passive-vulnerability-lookup'
    || mod === 'endpoint-connector'
    || fp.startsWith('connector-passive-vulnerability-lookup:')
    || fp.startsWith('endpoint-cve:');
  return isEndpoint && hasCveEvidence(f);
}

export function isVulnerabilityListFinding(f) {
  return isNessusScanFinding(f) || isShodanVulnerabilityFinding(f) || isEndpointVulnerabilityFinding(f);
}

export function isOsintFinding(f) {
  if (isSddFinding(f) || hasAdSource(f) || hasCloudAssessmentSource(f)) return false;
  const mod = (f?.module ?? f?.sourceModule ?? '').toLowerCase();
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const text = findingText(f).toLowerCase();
  const credentialType = (f?.detail?.additionalData?.credentialType ?? f?.additionalData?.credentialType ?? '').toLowerCase();

  return mod.includes('osint')
    || mod.includes('securitytrails')
    || mod.includes('shodan')
    || mod.includes('certspotter')
    || mod.includes('whois')
    || mod.includes('breach')
    || mod.includes('dehashed')
    || mod.includes('hibp')
    || fp.startsWith('osint-')
    || fp.startsWith('osint:')
    || fp.startsWith('breach-')
    || fp.includes(':osint:')
    || fp.includes(':breach:')
    || Boolean(credentialType)
    || text.includes('credential exposure')
    || text.includes('exposed credential')
    || text.includes('leaked credential')
    || text.includes('breached credential')
    || text.includes('password exposed')
    || text.includes('dark web')
    || text.includes('external exposure')
    || text.includes('dmarc')
    || text.includes('tls report')
    || text.includes('whois');
}

export function isCloudFinding(f) {
  if (isSddFinding(f) || hasAdSource(f) || isOsintFinding(f)) return false;
  const mod = (f?.module ?? f?.sourceModule ?? '').toLowerCase();
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const text = findingText(f).toLowerCase();

  return mod.includes('aws')
    || mod.includes('azure')
    || mod.includes('cloud')
    || mod.includes('cspm')
    || mod === 'm365-baseline'
    || mod === 'm365-drift'
    || fp.startsWith('cloud-')
    || fp.startsWith('aws-')
    || fp.startsWith('azure-')
    || fp.startsWith('m365-')
    || text.includes('arn:aws')
    || text.includes('cloudwatch')
    || text.includes('cloudtrail')
    || text.includes('entra')
    || text.includes('subscription')
    || text.includes('microsoft 365')
    || text.includes('conditional access')
    || text.includes('key vault');
}

export function isADFinding(f) {
  if (isSddFinding(f)) return false;
  const mod = (f?.module ?? f?.sourceModule ?? '').toLowerCase();
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const text = findingText(f).toLowerCase();

  if (hasAdSource(f)) return true;
  if (hasExternalScannerSource(f) || isOsintFinding(f) || isCloudFinding(f)) return false;

  return text.includes('domain admin')
    || text.includes('enterprise admin')
    || text.includes('dcsync')
    || text.includes('kerberoast')
    || text.includes('adminsdholder')
    || text.includes('unconstrained delegation')
    || text.includes('gpo')
    || text.includes('group policy')
    || text.includes('ldap signing')
    || text.includes('smb signing');
}

export function isDastFinding(f) {
  if (isSddFinding(f) || isCloudFinding(f) || isADFinding(f)) return false;
  const mod = (f?.module ?? f?.sourceModule ?? '').toLowerCase();
  const fp = (f?.fingerprint ?? '').toLowerCase();
  const text = findingText(f).toLowerCase();

  return mod.startsWith('dast')
    || mod.includes('zap')
    || mod.includes('nuclei')
    || mod.includes('web-scan')
    || fp.startsWith('dast-')
    || fp.includes(':zap:')
    || fp.includes(':nuclei:')
    || text.includes('cross-site scripting')
    || text.includes('sql injection')
    || text.includes('openapi')
    || text.includes('content security policy')
    || text.includes('strict-transport-security')
    || text.includes('hsts');
}
